import React, { FC } from 'react'

import { AuctionIcon, ChevronNextIcon, PhoneIcon } from '@svgs'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Button, TextAreaField } from 'src/components/ui'
import { IPropertyUser } from 'src/interfaces/IProperty'
import { useSendMessageMutation } from 'src/redux/socketApi'
import { Currency } from 'src/enums/Currency'
import InitialsAvatar from 'src/components/elements/initials-avatar/InitialsAvatar'

interface ISellerContact {
  user: IPropertyUser
  role: string
  isAuction: boolean
  currentPrice: number | null
  currency: Currency
}

const SellerContact: FC<ISellerContact> = ({
  user,
  role,
  isAuction,
  currentPrice = 0,
  currency,
}) => {
  const { t } = useTranslation()
  // const [sendChatMessage] = useSendChatMessageMutation()
  const [sendMessage] = useSendMessageMutation()

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    try {
      await sendMessage({ recipientId: user.id, content: form.messageText.value }).unwrap()
      // The text is cleared only once the server has the message.
      form.reset()
    } catch {
      toast.error(t('messenger_chat.send_failed'))
    }
  }

  return (
    <div className="flex overflow-hidden flex-wrap justify-between items-start self-stretch py-4 pr-4 mt-4 bg-brand-soft rounded-lg">
      <div className="flex overflow-hidden flex-col items-center px-1 py-px text-center rounded-lg w-[9.75rem] shrink-0 font-inter">
        <div className="w-[5.25rem] h-[5.25rem] relative">
          <div className="size-full rounded-full overflow-hidden">
            <InitialsAvatar
              src={user.avatar}
              name={`${user.firstName || ''} ${user.lastName || ''}`}
            />
          </div>
        </div>
        <div className="flex flex-col items-center mt-2 w-full">
          {/* Real presence only — the badge used to say "онлайн" for every
              seller, whatever their last activity. */}
          {user.isOnline && (
            <div className="flex gap-2 justify-center items-center px-2 py-1 text-xs text-white whitespace-nowrap bg-success-strong rounded">
              <i className="icon w-[.3125rem] h-[.3125rem] rounded-full bg-current shrink-0" />
              <span>{t('online')}</span>
            </div>
          )}
          <div className="flex flex-col mt-1.5 text-xs">
            <span className="font-semibold">
              {user.firstName} {user.lastName}
            </span>
            <span>{t(`step1.${role.toLowerCase()}`)}</span>
          </div>
        </div>
      </div>
      <div className="flex flex-col flex-1 shrink basis-0">
        <h2 className="text-xl font-black leading-tight">{t('contact_the_seller')}</h2>
        <form className="flex flex-col mt-4 w-full gap-2" onSubmit={handleSubmit}>
          <TextAreaField
            className="!h-[3.9375rem] !border-0"
            name="messageText"
            placeholder={t('message_text')}
          />
          <div className="flex gap-2 items-center text-white">
            {user.phone && (
              <Button
                link={`tel:${user.phone}`}
                className="flex-1 !gap-0 !bg-success-strong hover:!bg-success-strong/90"
                icon={<PhoneIcon />}
              >
                {user.phone}
              </Button>
            )}
            <Button className="flex-1 !gap-0" iconAfter={<ChevronNextIcon />} type="submit">
              {t('send_message')}
            </Button>
          </div>
          {isAuction && (
            <div className="flex-col gap-2 flex w-full">
              <div className="self-center text-center  text-lg font-black leading-snug">
                Запропонуйте свою ціну
              </div>
              <div className="self-stretch h-10 pl-2 pr-1 py-1 bg-surface rounded-xl border border-ink border-opacity-10 justify-between items-center inline-flex">
                <div className="px-1.5 py-[.1875rem] bg-success-strong rounded flex-col justify-center items-center gap-2.5 inline-flex">
                  <div className="w-4 h-4 icon-button fill-white">
                    <AuctionIcon />
                  </div>
                </div>
                <div className="grow shrink basis-0 justify-center items-baseline gap-1 flex">
                  <div className="text-lg font-black leading-[1.1069rem]">{currentPrice}</div>
                  <div className="text-center text-xs font-normal font-inter">{currency}</div>
                </div>
                <Button className="!h-8 !py-1">{t('agent.offer')}</Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default SellerContact
